<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'

import type { SwordDrillSyncState, SwordDrillTheme, SwordDrillVerse } from '~/stores/swordDrill'

import SwordDrillSetupScreen from '~/components/SwordDrill/SetupScreen.vue'
import { GAME_REGISTRY } from '~/utils/gameRegistry'

definePageMeta({
  layout: 'games',
  prerender: true,
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useSwordDrillStore()
const toast = useToast()
const { trackEvent } = useAnalytics()

// Defer view-mode detection to post-mount so the server-rendered (controller)
// HTML and the initial client hydration tree always agree. Without this, loading
// the page with ?view=presenter would produce Vue hydration mismatch warnings
// because the prerendered HTML never contains query params.
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})

const viewMode = computed<'controller' | 'presenter'>(() => {
  if (!mounted.value) {
    return 'controller'
  }
  return route.query.view === 'presenter' ? 'presenter' : 'controller'
})
const isControllerView = computed(() => viewMode.value === 'controller')
const isPresenterView = computed(() => viewMode.value === 'presenter')

const {
  connected: presentationConnected,
  connecting: presentationConnecting,
  connectPresentationAsWindow,
  disconnectPeerPresenters,
  disconnectPresentation,
  lastError: presentationError,
  pairingCode,
  pairingUrl,
  peerPresentersCount,
  peerReady,
  peerTransportConnecting,
  popupPresenterConnected,
  presentationSupported,
  presenterEntryUrl,
  presenterLeave,
  presenterReconnect,
  presenterTargetCode,
  presenterTransportKind,
  regeneratePairingCode,
} = useGamePresentation<SwordDrillSyncState>({
  peerNamespace: GAME_REGISTRY['sword-drill'].peerNamespace,
  presenterChar: GAME_REGISTRY['sword-drill'].presenterChar,
  store: {
    applySyncState: state => store.applySyncState(state),
    getSyncState: () => store.getSyncState(),
  },
  viewMode,
})

// ── Setup screen ref (for header menu actions) ─────────
const setupRef = ref<InstanceType<typeof SwordDrillSetupScreen> | null>(null)

async function handleConnectPresentationWindow() {
  if (!presentationSupported.value) {
    toast.add({
      color: 'warning',
      description: gpUi.value?.unsupported,
      duration: 2800,
      icon: 'mdi:alert-circle-outline',
    })
    return
  }

  const opened = await connectPresentationAsWindow()
  if (opened) {
    return
  }

  if (presentationError.value) {
    toast.add({
      color: 'error',
      description: gpUi.value?.connectionFailed,
      duration: 3200,
      icon: 'mdi:alert-circle-outline',
    })
    return
  }

  toast.add({
    color: 'error',
    description: gpUi.value?.connectionFailed,
    duration: 3200,
    icon: 'mdi:alert-circle-outline',
  })
}

function handleDisconnectPeerPresenters() {
  disconnectPeerPresenters()
  toast.add({
    color: 'neutral',
    description: gpUi.value?.peerDisconnected,
    duration: 2200,
    icon: 'mdi:account-off-outline',
  })
}

function handleDisconnectPresentation() {
  disconnectPresentation()
}

function handleLaunchDisplayMode() {
  router.push({ query: { ...route.query, view: 'presenter' } })
}

function handlePresenterLeave() {
  presenterLeave()
}

function handleRegeneratePairingCode() {
  regeneratePairingCode()
  toast.add({
    color: 'neutral',
    description: gpUi.value?.pairingRegenerated,
    duration: 2000,
    icon: 'mdi:refresh',
  })
}

function handleReset() {
  setupRef.value?.handleReset()
}

// ── Load all content ──────────────────────────────────────
const [ { data: versesContent }, { data: themesContent }, { data: ui }, { data: gpUi } ] = await Promise.all([
  useAsyncData(
    'sword-drill-verses',
    () => queryCollection('swordDrillVerses').first(),
  ),
  useAsyncData(
    'sword-drill-themes',
    () => queryCollection('swordDrillThemes').first(),
  ),
  useSwordDrillUI(),
  useGamePresentationUI(),
])

watch(presentationConnected, (isNowConnected, wasConnected) => {
  if (!isControllerView.value || !wasConnected || isNowConnected) {
    return
  }
  toast.add({
    color: 'warning',
    description: gpUi.value?.disconnectedStatus,
    duration: 2500,
    icon: 'mdi:monitor-off',
  })
})

const swordSeo = computed(() => ui.value?.page.seo)
const swordTitle = computed(() => swordSeo.value?.title ?? ui.value?.page.title)
const swordDescription = computed(() => swordSeo.value?.description ?? ui.value?.page.description)
const swordOgTitle = computed(() => swordSeo.value?.ogTitle ?? swordTitle.value)
const swordOgDescription = computed(() => swordSeo.value?.ogDescription ?? swordDescription.value)

useSeoMeta({
  description: () => swordDescription.value,
  ogDescription: () => swordOgDescription.value,
  ogTitle: () => swordOgTitle.value,
  title: () => swordTitle.value,
  twitterDescription: () => swordOgDescription.value,
  twitterTitle: () => swordOgTitle.value,
})

defineOgImageComponent('Default', {
  description: () => swordOgDescription.value ?? '',
  eyebrow: 'signalfire.games / sword drill',
  title: () => swordOgTitle.value ?? 'Sword Drill',
})

// ── URL-driven game phase resume ──────────────────────────
// Mirrors Showdown's pattern: the setup ↔ playing transition is reflected in
// `?phase=playing` so back returns to setup, and forward (or reload) restores
// the in-progress game from localStorage. A bare URL is always the setup
// landing, even if localStorage has stale game state.
const persistedSyncState = useLocalStorage<null | SwordDrillSyncState>(
  'sfg.swordDrill.gameState',
  null,
  { serializer: { read: v => v ? JSON.parse(v) : null, write: v => JSON.stringify(v) } },
)

// Re-attach the loaded content to the store after a refresh so the next
// "next verse" call has a pool to draw from.
function rehydrateStoreData() {
  const v = (versesContent.value?.verses ?? []) as SwordDrillVerse[]
  const t = (themesContent.value?.themes ?? []) as SwordDrillTheme[]
  store.setVerses(v)
  store.setThemes(t)
}

function restoreFromPersistedIfAny() {
  if (!isControllerView.value) {
    return
  }
  if (route.query.phase === 'playing' && persistedSyncState.value && store.phase === 'setup') {
    resumePersistedGame()
  }
}

function resumePersistedGame() {
  if (!persistedSyncState.value) {
    return
  }
  rehydrateStoreData()
  store.applySyncState(persistedSyncState.value)
}

if (import.meta.client) {
  watch(() => store.getSyncState(), state => {
    if (isControllerView.value && state.phase !== 'setup') {
      persistedSyncState.value = state
    }
  }, { deep: true })

  watch(() => route.query.phase, q => {
    if (!isControllerView.value) {
      return
    }
    if (q === 'playing') {
      if (persistedSyncState.value && store.phase === 'setup') {
        resumePersistedGame()
      }
    } else if (store.phase !== 'setup') {
      store.exitGame()
    }
  })

  onMounted(() => {
    restoreFromPersistedIfAny()
  })
}

// ── Game reset hook ────────────────────────────────────
const { register: registerReset } = useGameReset()
registerReset(() => {
  if (import.meta.client && document.fullscreenElement) {
    document.exitFullscreen().catch(() => {})
  }
  store.exitGame()
  if (route.query.phase) {
    void router.replace({ query: { ...route.query, phase: undefined } })
  }
})

// ── Take control: promote presenter to controller in this tab ──
function takeControl() {
  if (!isPresenterView.value || presenterTransportKind.value !== 'peer') {
    return
  }
  persistedSyncState.value = store.getSyncState()
  const next = { ...route.query } as Record<string, string>
  delete next.view
  delete next.code
  delete next.session
  delete next.autofs
  next.phase = 'playing'
  void router.replace({ query: next })
}

// ── Presence context (drives GameFab's connection / status / exit UI) ──
provideGamePresence({
  connected: presentationConnected,
  connecting: peerTransportConnecting,
  lastError: presentationError,
  pairingCode,
  peerPresentersCount,
  presenterTargetCode,
  presenterTransportKind,
  retryConnection: presenterReconnect,
  takeControl,
  viewMode,
})

// ── Game lifecycle ───────────────────────────────────────
function handleStart() {
  if (!isControllerView.value) {
    return
  }
  rehydrateStoreData()

  // Match Showdown: request fullscreen synchronously (in user-gesture context)
  // unless dev or a presentation window is already connected.
  if (!presentationConnected.value && import.meta.client && !import.meta.dev) {
    document.documentElement.requestFullscreen().catch(() => {})
  }
  store.startGame()
  void router.push({ query: { ...route.query, phase: 'playing' } })
  trackEvent('game_started', {
    game: 'sword-drill',
    mode: store.config.mode,
  })
}
</script>

<template>
  <div
    v-if="isPresenterView"
    class="h-screen w-screen bg-default text-default"
  >
    <GamePresentationPresenterPairing v-if="presenterTransportKind === 'unpaired'"/>

    <SwordDrillGameScreen
      v-else-if="store.phase !== 'setup'"
      :interactive="false"
      :show-answers-by-default="false"
    />
    <div
      v-else
      class="flex h-full items-center justify-center px-6"
    >
      <div class="w-full max-w-3xl">
        <UCard :ui="{ body: 'bg-muted/5 text-center' }">
          <div class="flex flex-col items-center gap-5 py-6 sm:py-8">
            <UIcon
              class="size-14 text-primary"
              :name="presentationConnected ? 'mdi:check-circle-outline' : 'mdi:progress-clock'"
            />
            <h1 class="text-balance text-3xl font-black tracking-tight sm:text-4xl">
              {{ presentationConnected ? gpUi?.presenterWaitingTitle : gpUi?.presenterConnectingTitle }}
            </h1>
            <p class="max-w-2xl text-balance text-base text-muted sm:text-lg">
              {{ presentationConnected ? gpUi?.presenterWaitingDescription : gpUi?.presenterConnectingDescription }}
            </p>
          </div>
        </UCard>
      </div>
    </div>

    <GamePresentationPresenterFullscreenOverlay v-if="presenterTransportKind === 'peer' && presentationConnected"/>

    <!-- Pairing/setup screens get the prominent SfgChromeButton in the
         top-left. Once the room is in playing mode, the GameFab in the
         bottom-left corner provides the leave action so the
         presentation surface stays clean. -->
    <SfgChromeButton
      v-if="presenterTransportKind === 'unpaired' || store.phase === 'setup'"
      class="fixed top-4 left-4 z-40"
      :icon-left="presenterTransportKind === 'broadcast' ? 'mdi:close' : 'mdi:arrow-left'"
      @click="handlePresenterLeave"
    >
      {{ presenterTransportKind === 'broadcast' ? gpUi?.presenterCloseButton : gpUi?.presenterLeaveButton }}
    </SfgChromeButton>
  </div>

  <!-- Setup screen -->
  <div
    v-else-if="store.phase === 'setup'"
    class="flex min-h-screen flex-col items-center justify-start p-6 xl:justify-center"
  >
    <SfgChromeButton
      to="/games"
      icon-left="mdi:arrow-left"
      class="self-start mb-4 z-30 xl:fixed xl:top-4 xl:left-4 xl:mb-0"
    >
      {{ t('setupMenu.backToLibrary') }}
    </SfgChromeButton>
    <div class="w-full max-w-3xl">
      <SfgGameSetupCard
        icon="twemoji:books"
        :title="ui?.page.title ?? ''"
        :description="ui?.page.description ?? ''"
        :loading-text="ui?.setup.loading"
        :reset="handleReset"
      >
        <SwordDrillSetupScreen
          ref="setupRef"
          :pairing-code="pairingCode"
          :pairing-url="pairingUrl"
          :peer-presenters-count="peerPresentersCount"
          :peer-ready="peerReady"
          :popup-presenter-connected="popupPresenterConnected"
          :presentation-connecting="presentationConnecting"
          :presentation-supported="presentationSupported"
          :presenter-entry-url="presenterEntryUrl"
          @start="handleStart"
          @connect-presentation-window="handleConnectPresentationWindow"
          @disconnect-peer-presenters="handleDisconnectPeerPresenters"
          @disconnect-presentation="handleDisconnectPresentation"
          @launch-display-mode="handleLaunchDisplayMode"
          @regenerate-pairing-code="handleRegeneratePairingCode"
        />
      </SfgGameSetupCard>
    </div>
  </div>

  <!-- Game screen, fullscreen, no site chrome -->
  <SwordDrillGameScreen
    v-else
    :interactive="true"
    :presentation-connected="presentationConnected"
    :show-answers-by-default="presentationConnected"
  />

  <GameFab v-if="store.phase !== 'setup'"/>

  <!-- Help modal: opened from the setup-screen kebab menu and the
       in-game FAB. Mounted at the page root so it's reachable from
       both the setup and game phases without re-mounting. -->
  <GameHelpModal
    :title="ui?.help.title ?? 'Help'"
    :sections="ui?.help.sections ?? []"
  />
</template>
